import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { apiGet, apiPost, formatEuro } from "../../lib/api";
import {
  EMPTY_NEW_PRODUCT,
  buildAdminAuthHeaders,
  isUnauthorizedAdminError,
  shortProductId,
  toProductDraft,
  toProductPayload
} from "./admin-utils";
import "../Shop.css";

const AdminProductsPage = () => {
  const { adminToken, handleUnauthorized } = useOutletContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [newProductDraft, setNewProductDraft] = useState(() => ({ ...EMPTY_NEW_PRODUCT }));
  const [isProductsLoading, setIsProductsLoading] = useState(true);
  const [isProductsRefreshing, setIsProductsRefreshing] = useState(false);
  const [isCreatingProduct, setIsCreatingProduct] = useState(false);
  const [error, setError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");

  const loadProducts = useCallback(
    async ({ refreshOnly = false } = {}) => {
      if (!adminToken) {
        return;
      }

      setError("");
      if (refreshOnly) {
        setIsProductsRefreshing(true);
      } else {
        setIsProductsLoading(true);
      }

      try {
        const payload = await apiGet("/api/admin/products", {
          headers: buildAdminAuthHeaders(adminToken)
        });

        const nextProducts = Array.isArray(payload?.products) ? payload.products : [];
        setProducts(nextProducts);
      } catch (loadError) {
        const message = loadError instanceof Error ? loadError.message : "Errore caricamento prodotti";
        if (isUnauthorizedAdminError(loadError)) {
          handleUnauthorized();
          return;
        }
        setError(message);
      } finally {
        if (refreshOnly) {
          setIsProductsRefreshing(false);
        } else {
          setIsProductsLoading(false);
        }
      }
    },
    [adminToken, handleUnauthorized]
  );

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    const notice = location.state?.notice;
    if (!notice) {
      return;
    }

    setSaveMessage(String(notice));
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  const handleNewProductDraftChange = (field, value) => {
    setNewProductDraft((current) => ({
      ...current,
      [field]: value
    }));
  };

  const handleCreateProduct = async (event) => {
    event.preventDefault();
    setIsCreatingProduct(true);
    setError("");
    setSaveMessage("");

    try {
      await apiPost("/api/admin/products", toProductPayload(newProductDraft), {
        headers: buildAdminAuthHeaders(adminToken)
      });
      setNewProductDraft({ ...EMPTY_NEW_PRODUCT });
      setSaveMessage("Prodotto creato.");
      await loadProducts({ refreshOnly: true });
    } catch (createError) {
      const message = createError instanceof Error ? createError.message : "Errore creazione prodotto";
      if (isUnauthorizedAdminError(createError)) {
        handleUnauthorized();
        return;
      }
      setError(message);
    } finally {
      setIsCreatingProduct(false);
    }
  };

  return (
    <section className="admin-section-block">
      <div className="admin-section-header">
        <h3 className="admin-section-title">Prodotti</h3>
        <button
          className="cart-clear-button"
          type="button"
          onClick={() => loadProducts({ refreshOnly: true })}
          disabled={isProductsRefreshing || isProductsLoading}
        >
          {isProductsRefreshing ? "Aggiornamento..." : "Ricarica prodotti"}
        </button>
      </div>

      {error && <p className="shop-state shop-error">Errore: {error}</p>}
      {saveMessage && <p className="shop-state admin-success">{saveMessage}</p>}

      <form className="admin-product-create-form" onSubmit={handleCreateProduct}>
        <div className="admin-product-create-grid">
          <input
            className="admin-form-input"
            type="text"
            placeholder="Nome"
            value={newProductDraft.name}
            onChange={(event) => handleNewProductDraftChange("name", event.target.value)}
            required
          />
          <input
            className="admin-form-input"
            type="text"
            placeholder="Slug (es. olio-evo-750ml)"
            value={newProductDraft.slug}
            onChange={(event) => handleNewProductDraftChange("slug", event.target.value)}
            required
          />
          <input
            className="admin-form-input"
            type="number"
            min="1"
            step="1"
            placeholder="Prezzo centesimi"
            value={newProductDraft.price_cents}
            onChange={(event) => handleNewProductDraftChange("price_cents", event.target.value)}
            required
          />
          <input
            className="admin-form-input"
            type="number"
            min="1"
            step="1"
            placeholder="Peso grammi"
            value={newProductDraft.weight_grams}
            onChange={(event) => handleNewProductDraftChange("weight_grams", event.target.value)}
            required
          />
          <input
            className="admin-form-input"
            type="number"
            min="0"
            step="1"
            placeholder="Stock"
            value={newProductDraft.stock_qty}
            onChange={(event) => handleNewProductDraftChange("stock_qty", event.target.value)}
            required
          />
          <input
            className="admin-form-input"
            type="url"
            placeholder="Image URL (opzionale)"
            value={newProductDraft.image_url}
            onChange={(event) => handleNewProductDraftChange("image_url", event.target.value)}
          />
          <input
            className="admin-form-input admin-form-input-wide"
            type="text"
            placeholder="Descrizione (opzionale)"
            value={newProductDraft.description}
            onChange={(event) => handleNewProductDraftChange("description", event.target.value)}
          />
          <label className="admin-checkbox-label">
            <input
              type="checkbox"
              checked={newProductDraft.active}
              onChange={(event) => handleNewProductDraftChange("active", event.target.checked)}
            />
            Attivo
          </label>
        </div>
        <button type="submit" className="shop-add-button admin-create-button" disabled={isCreatingProduct}>
          {isCreatingProduct ? "Creazione..." : "Crea prodotto"}
        </button>
      </form>

      {isProductsLoading && <p className="shop-state">Caricamento prodotti...</p>}
      {!isProductsLoading && products.length === 0 && <p className="shop-state">Nessun prodotto disponibile.</p>}

      {!isProductsLoading && products.length > 0 && (
        <div className="admin-table-wrap">
          <table className="admin-products-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nome / Slug</th>
                <th>Prezzo</th>
                <th>Peso</th>
                <th>Stock</th>
                <th>Attivo</th>
                <th>Azione</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => {
                const productDraft = toProductDraft(product);

                return (
                  <tr key={product.id}>
                    <td>
                      <strong>{shortProductId(product.id)}</strong>
                      <div className="admin-row-meta">{product.id}</div>
                    </td>
                    <td>
                      <div className="admin-row-primary">{product.name}</div>
                      <div className="admin-row-meta">{product.slug}</div>
                      {product.description && <div className="admin-row-meta">{product.description}</div>}
                    </td>
                    <td>
                      <div className="admin-row-primary">{formatEuro(Number(productDraft.price_cents) || 0)}</div>
                      <div className="admin-row-meta">{productDraft.price_cents} cent</div>
                    </td>
                    <td>{product.weight_grams} g</td>
                    <td>{product.stock_qty}</td>
                    <td>{product.active ? "Si" : "No"}</td>
                    <td>
                      <Link
                        className="shop-add-button admin-save-button"
                        to={`/admin/products/${encodeURIComponent(product.id)}/edit`}
                      >
                        Modifica
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default AdminProductsPage;
